<template>
	<div class="variant-picker">
		<template v-for="(group, index) in formattedProducts">
			<h2>
				{{ group.name }}
				<template v-if="hoverColorName.length && group.type === 'color'">
					- {{ hoverColorName }}
				</template>
			</h2>
			<radio-picker
				v-if="group.type === 'text'"
				v-model="radioPick[group.name]"
				:key="'variant-radio-picker-' + index"
				:options="group.radioValues"
				@input="(value) => radioRoute(value, index)"
			/>
			<div v-else class="image-picker">
				<router-link
					v-for="(product, imgIndex) of group.products"
					:class="{ current: product.value === currentProductData[group.name], disabled: ! product.match }"
					:key="'variant-picker-image-' + imgIndex"
					:to="product.route"
					@click.native="clickedIndex = imgIndex"
					@mouseover.native="hoverColorName = product.value"
					@mouseleave.native="hoverColorName = currentProductData[group.name]"
				>
					<ximg :src="product.imgSrc" :ariaLabel="product.value" role="img" />
				</router-link>
			</div>
		</template>
	</div>
</template>

<script>
import { formatPrice, getProductPath, inStockAnywhere, convertToCdnUrl } from 'utils';
import { trackImpression } from 'gtm';

export default {
	props: {
		variantGroups: {
			type: Array,
			default: () => [],
			required: true,
		},
		variantData: {
			type: Array,
			default: () => [],
			required: true,
		},
		currentProductId: {
			type: Number,
			default: 0,
			required: true,
		},
		currentProductPrice: {
			type: Object,
			default: () => {},
			required: true,
		},
		currentProduct: {
			type: Object,
			default: () => {},
			required: true,
		},
	},
	data () {
		return {
			radioPick: {},
			hoverColorName: '',
			formattedProducts: null,
			clickedIndex: 0,
		};
	},
	computed: {
		currentProductData () {
			const current = this.variantData.find((product) => {
				if (product.id === this.currentProductId) {
					return product;
				}
			});

			return current?.variantProperties;
		},
	},
	mounted () {
		this.formatProducts();
	},
	methods: {
		formatProducts () {
			let mappedVariantProducts = [];
			this.variantGroups.forEach((group, index) => {
				mappedVariantProducts[index] = {
					name: group.name,
					type: group.type,
					products: [],
					radioValues: [],
					radioPrices: [],
				};

				mappedVariantProducts[index].products = group.values.reduce((accumulator, value) => {
					let match = true;
					let matchingProduct = this.variantData.find((product) => {
						return product.variantProperties && JSON.stringify(product.variantProperties).toLowerCase().replaceAll(/\s/g,'') === JSON.stringify({...this.currentProductData, [group.name]: value }).toLowerCase().replaceAll(/\s/g,'');
					});

					/** If there is no product matching witch current specs,
					 * we still need some data to display the picker nicely
					 */
					if (! matchingProduct) {
						matchingProduct = this.variantData.find((product) => {
							return product.variantProperties && product.variantProperties[group.name].toLowerCase().replaceAll(/\s/g,'') === value.toLowerCase().replaceAll(/\s/g,'');
						});
						match = false;
					}

					/* Check for fyndwares and discontinued products without stock */
					if (matchingProduct.isFyndware || (matchingProduct.discontinued && ! inStockAnywhere(matchingProduct.stock))) {
						/** Check if there is any product that has the current variant value,
						 * if yes, just disable the selection like the previous check,
						 * if no, remove it entierly
						 */
						const eligableVariant = this.variantData.find((product) => {
							return product.variantProperties
								&& product.variantProperties[group.name].toLowerCase().replaceAll(/\s/g, '') === value.toLowerCase().replaceAll(/\s/g, '')
								&& product.id !== matchingProduct.id
								&& ! product.isFyndware && (! product.discontinued || (product.discontinued && inStockAnywhere(product.stock)));;
						});

						if (! eligableVariant) {
							return accumulator;
						} else {
							match = false;
						}
					}

					/* Format the products for radio picker */
					if (group.type === 'text') {
						let radioObject = {
							label: matchingProduct.variantProperties[group.name],
							rightText: '',
							disabled: ! match,
						};

						if (match) {
							const price = parseFloat(matchingProduct.price.price) - parseFloat(this.currentProductPrice.price);
							const pricePrefix = price > 0 ? '+' : '';

							radioObject.rightText = price ? pricePrefix + formatPrice(price, this.currentProductPrice.currency) : '';
						} else {
							radioObject.rightText = 'Kombinationen finns inte';
						}

						mappedVariantProducts[index].radioValues.push(radioObject);
					}

					accumulator.push({
						id: matchingProduct.id,
						price: match ? matchingProduct.price.price : '',
						route: match ? getProductPath(matchingProduct) : {},
						imgSrc: convertToCdnUrl('/images/product/' + matchingProduct.id + '?trim&h=50'),
						value: matchingProduct.variantProperties[group.name],
						match,
					});
					return accumulator;
				}, []);
			});

			// Loop to sort variants based on price
			mappedVariantProducts.forEach(variantProduct => {
				// Sort products by price
				let sortedProducts = variantProduct.products.sort((a, b) => a.price - b.price);

				// Extract order of product values after sorting
				let sortedProductValues = sortedProducts.map(product => product.value);

				// Sort radioValues to match the sorted order of products
				variantProduct.radioValues.sort((a, b) => {
					return sortedProductValues.indexOf(a.label) - sortedProductValues.indexOf(b.label);
				});
			});

			this.formattedProducts = mappedVariantProducts;
			this.preSelectCurrentSpecs(mappedVariantProducts);
		},
		preSelectCurrentSpecs (products) {
			products.forEach((item) => {
				if (item.type === 'color') {
					this.hoverColorName = this.currentProductData['Färg'];
				} else {
					for (let i = 0; i < item.radioValues.length; i++) {
						if (this.currentProductData[item.name].toLowerCase().replaceAll(/\s/g,'') === item.radioValues[i].label.toLowerCase().replaceAll(/\s/g,'')) {
							this.radioPick[item.name] = i;
							return;
						}
					}
				}
			});
		},
		radioRoute (value, groupIndex) {
			this.clickedIndex = value;
			if (this.currentProductId !== this.formattedProducts[groupIndex].products[value].id) {
				this.$router.replace({params: { ...this.formattedProducts[groupIndex].products[value].route.params }});
			}
		},
		track (product, index) {
			trackImpression([product], 'Item Variant', {
				rating: true,
				stock: true,
				event: 'select_item',
				position: index
			});
		},
	},
	watch: {
		currentProductId () {
			this.formatProducts();
			this.track(this.currentProduct, this.clickedIndex);
		},
	},
};
</script>
